/**
 * Bulk registration — done.
 *
 * One line per event with its status. In popup mode, posts the same
 * tn:registration:complete message the single flow posts (with every
 * registration) so loader.js can close the modal and host pages can react.
 */
import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import type { DisplayMode } from '@/App';
import type { EmbedClient } from '@/api/embed';
import { useFlow } from '@/state/FlowContext';
import { ParentLinks } from '@/components/ParentLink';
import { useDashboardLink } from '@/state/dashboardLink';
import { useAccountPromotion } from '@/state/useAccountPromotion';
import { STATUS_LABEL } from './bulkFormat';
import './DonePage.css';
import './Bulk.css';

interface Props {
  client: EmbedClient;
  displayMode: DisplayMode;
}

export function BulkDonePage({ client, displayMode }: Props) {
  const navigate = useNavigate();
  const { bulkBatch, bulkConfirmed, resetBulk, createAccount, selectedTeam } = useFlow();

  // Paid batches carry the confirmed view; an all-approval batch never paid.
  const regs = bulkConfirmed.length ? bulkConfirmed : (bulkBatch?.registrations ?? []);
  const confirmed = regs.filter((r) => r.status === 'confirmed').length;
  const awaiting = regs.filter((r) => r.status === 'pending_approval').length;
  // Registered with Pay later: spots held, each entry fee still due.
  const [params] = useSearchParams();
  const payLater = params.get('pay_later') === '1';
  const held = regs.filter((r) => r.status === 'pending_payment').length;
  // Pay later is paid from My Teams, so a guest who chose it gets the account.
  const promote = (createAccount || payLater) && (confirmed > 0 || (payLater && held > 0));
  // "Save my team for next time" (checked on Review): create the
  // TourneyNation account once something is registered, not on approval.
  const { status: accountStatus } = useAccountPromotion(client, promote);
  const dashboard = useDashboardLink(client, accountStatus, promote);

  useEffect(() => {
    if (regs.length === 0) {
      navigate('/bulk/events', { replace: true });
      return;
    }
    if (displayMode !== 'popup') return;
    try {
      window.parent.postMessage(
        {
          type: 'tn:registration:complete',
          // Stay open: Done has the parent link and the dashboard.
          autoClose: false,
          registrations: regs.map((r) => ({ id: r.id, status: r.status, event_id: r.event_id })),
        },
        '*',
      );
    } catch {
      // Non-fatal: the host just won't auto-close.
    }
    // Post once on arrival.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (regs.length === 0) return null;

  const registered = payLater ? confirmed + held : confirmed;
  const title = registered === regs.length
    ? `You're registered for ${registered} event${registered === 1 ? '' : 's'}`
    : registered > 0
      ? `Registered for ${registered} of ${regs.length} events`
      : 'Submitted for approval';

  return (
    <div className="tn-done">
      <h1 className="tn-done__title">{title}</h1>
      <p className="tn-done__subtitle">
        {payLater && held > 0
          ? `Your spot${held === 1 ? ' is' : 's are'} held. Pay ${held === 1 ? 'the entry fee' : 'each entry fee'} by card before the tournament; we emailed you how.${
            awaiting > 0 ? ' We’ll also email you when the organizer reviews the rest.' : ''}`
          : awaiting > 0
            ? 'We’ll email you when the organizer reviews the rest.'
            : 'A confirmation email is on its way.'}
      </p>

      {/* Same rows as the single done screen: one per event, with its status. */}
      <ul className="tn-done__regs">
        {regs.map((r) => (
          <li key={r.id} className="tn-done__reg">
            <span className="tn-done__reg-text">
              <span className="tn-done__reg-name">{r.event_name}</span>
              {r.division ? <span className="tn-done__reg-sub">{r.division}</span> : null}
            </span>
            <span className={`tn-done__status tn-done__status--${r.status}`}>
              {STATUS_LABEL[r.status] ?? r.status.replace(/_/g, ' ')}
            </span>
          </li>
        ))}
      </ul>

      {selectedTeam ? (
        <ParentLinks
          client={client}
          inEmail={confirmed > 0}
          items={regs.map((r) => ({ teamId: selectedTeam.id, registrationId: r.id, eventName: r.event_name ?? 'the tournament' }))}
        />
      ) : null}

      <div className="tn-done__actions">
        {dashboard ? (
          <a className="tn-btn tn-btn--primary" href={dashboard} target="_blank" rel="noopener noreferrer">
            Take me to my dashboard
          </a>
        ) : null}
        <button
          type="button"
          className="tn-btn tn-btn--secondary"
          onClick={() => {
            resetBulk();
            navigate('/bulk/teams');
          }}
        >
          Register another team
        </button>
      </div>
    </div>
  );
}
